import type { SessionAdvance } from '@matter_extractor/shared';

/**
 * THE WAIT AT THE BUTTON (noyau.md §4.6, N5, N6), as a pure machine.
 *
 * An advance whose destination depends on the noyau, while its determination
 * is still running, is refused `noyau-pending` (409) and the session does not
 * move. No request is held open: the button keeps saying its waiting label
 * (« Un instant... » by default, a Bouton's own since #174), and
 * the advance is REPLAYED when the `noyau` frame of the session's channel
 * announces the determination settled - the page of choice therefore never
 * has a loading state.
 *
 * ## The frame can arrive BEFORE the refusal
 *
 * The 409 and the frame travel on two connections. A determination that
 * settles between the moment the server refused and the moment the refusal
 * lands would send its frame first - and a client that only started waiting at
 * the refusal would wait for a frame that has already gone by. So a frame seen
 * since the press is REMEMBERED (`settledSincePress`), and the refusal that
 * follows it is replayed at once. The press resets it: a frame from before the
 * press says nothing of it. Every `noyau` frame means « settled for good » -
 * the channel also sends one at each connection once it is - so remembering
 * one can never be wrong.
 *
 * Pure and outside the route, for the reason `session-conversation.ts` gives
 * for the thread: what a frame does to a gesture in flight is the part a
 * render test cannot pin down.
 */

export type NoyauWait = {
  /** The advance refused `noyau-pending`, held until the frame; null otherwise. */
  readonly held: SessionAdvance | null;
  /** A `noyau` frame landed since the last press. */
  readonly settledSincePress: boolean;
};

export const NO_NOYAU_WAIT: NoyauWait = { held: null, settledSincePress: false };

/** What the wait decides: its next state, and the advance to post again NOW, if any. */
export type NoyauWaitStep = {
  readonly wait: NoyauWait;
  readonly replay: SessionAdvance | null;
};

/** An advance leaves: nothing is held, and no frame has been seen for it yet. */
export function noyauWaitPressed(): NoyauWait {
  return NO_NOYAU_WAIT;
}

/** The advance came back `noyau-pending`: hold it - or replay it, if the frame beat the refusal. */
export function noyauWaitRefused(wait: NoyauWait, advance: SessionAdvance): NoyauWaitStep {
  if (wait.settledSincePress) return { wait: NO_NOYAU_WAIT, replay: advance };
  return { wait: { held: advance, settledSincePress: false }, replay: null };
}

/** A `noyau` frame landed: the held advance is replayed, or the frame is remembered. */
export function noyauWaitSettled(wait: NoyauWait): NoyauWaitStep {
  if (wait.held !== null) return { wait: NO_NOYAU_WAIT, replay: wait.held };
  return { wait: { held: null, settledSincePress: true }, replay: null };
}

/** Whether the button is WAITING - held on the determination, between two requests. */
export function isNoyauWaiting(wait: NoyauWait): boolean {
  return wait.held !== null;
}
